import { Alert, Box, Card, CardContent, Grid, Typography } from "@mui/material";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ButtonRetry } from "components/buttons/ButtonRetry";
import HtmlUtils from "lib/HtmlUtils";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { MprcMessageBox } from "./MprcMessageBox";

////////////////////////////////////////////////////////////////////////////////

export type MprcRequestError = Error | AxiosError | ApiProblemDetails;

////////////////////////////////////////////////////////////////////////////////

export default class RequestErrorUtils {

    static isApplicationError(error: any): boolean {
        if ('type' in error) {
            if (error && error.status == 409 && error.detail) {
                return true;
            }
        }

        return false;
    }

    static findValidationMessage(error: any, validationParam: string, codeError: string): string | null {
        let errorMessage: string | null = null;

        if ('type' in error) {
            if (error && error.status == 406
                && error.errors && error.extensions
                && error.errors.hasOwnProperty(validationParam)
                && error.extensions.hasOwnProperty(validationParam)) {
                if (error.errors[validationParam].length > 0 && error.extensions[validationParam] == codeError) {
                    errorMessage = HtmlUtils.textToHtml(error.errors[validationParam][0]);
                }
            }
        }

        return errorMessage;
    }

    static messageValidationErrors(errorMessages?: Record<string, string[]>): string[] {

        if (errorMessages == undefined) return [];

        var messages: string[] = [];
        for (const key in errorMessages) {
            if (errorMessages[key]) {
                for (const s of errorMessages[key]) {
                    messages.push(`${s} [${key}]`);
                }
            }
        }

        return messages;
    }
}

////////////////////////////////////////////////////////////////////////////////

export type MprcResolveRequestErrorResult = {
    fieldsWithError: ApiProblemErrors | undefined;
    otherErrors: MprcRequestError | undefined;
    errorCodes: string[] | undefined;
};

export const MprcResolveRequestErrors = (error: any): MprcResolveRequestErrorResult => {
    var result: MprcResolveRequestErrorResult = {
        fieldsWithError: undefined,
        otherErrors: undefined,
        errorCodes: undefined
    };

    var mainError = error as ApiProblemDetails;
    if ('type' in mainError && mainError.status == 406) {
        //  AppValidationException // ValidationException
        result.errorCodes = mainError.extensions
            ? Object.values(mainError.extensions).filter(value => value != "stackTrace" && value != "traceIdentifier")
            : [];

        var fieldErrors = mainError.errors
            ? Object.entries(mainError.errors).filter(([key, value]) => key != "")
            : [];
        if (fieldErrors.length > 0) result.fieldsWithError = Object.fromEntries(fieldErrors);

        var otherErrors = mainError.errors
            ? Object.entries(mainError.errors).filter(([key, value]) => key == "")
            : [];
        if (otherErrors.length > 0) {
            mainError.errors = Object.fromEntries(otherErrors);
            result.otherErrors = mainError;
        }
    }
    else {
        const ei = ExtractErrorInformation(error);
        result.otherErrors = { message: ei.message, stack: ei.stackTrace, name: ei.title } as Error;
    }
    return result;
};

////////////////////////////////////////////////////////////////////////////////

export const MprcRequestSnackbarErrorReport = (e: any) => {
    var r = ExtractFieldErrors(e);
    r.forEach(msg => enqueueSnackbar(msg, { variant: "error" }));
};

////////////////////////////////////////////////////////////////////////////////

export const MprcRequestAlertErrorReport = ({ errors }: { errors: any; }) => {
    var r = ExtractFieldErrors(errors);
    return r.length > 0 && (
        <Alert severity="error">
            {
                r.length == 1
                    ? r[0]
                    : (
                        <ul>
                            {r.map((msg, index) => <li key={index}>{msg}</li>)}
                        </ul>
                    )
            }
        </Alert>
    );
};

////////////////////////////////////////////////////////////////////////////////

export const ExtractFieldErrors = (e: any): string[] => {

    if (typeof (e) == "string")
        return [e];

    var r = MprcResolveRequestErrors(e);
    const fieldErrors: string[] = [];
    Object.entries(r.fieldsWithError ?? {}).forEach(([key, value]) => value.forEach(v => fieldErrors.push(v)));

    if (fieldErrors.length > 0) {
        return fieldErrors;
    }
    else {
        const error = ExtractErrorInformation(e);
        if (error.message) return [error.message];
    }

    return [];
};

export const ExtractErrorInformation = (error: MprcRequestError) => {
    var message: string | undefined;
    var stackTrace: string | undefined;
    var title: string | undefined = undefined;
    var traceIdentifier: string | undefined = undefined;
    var status: number | undefined = undefined;
    var errorList: ApiProblemErrors | undefined = undefined;

    if (axios.isAxiosError(error)) {
        if (error.code == "ERR_NETWORK") {
            status = 0;
            message = error.message;
            stackTrace = error.stack;
        }
        else {
            const response = error.response as AxiosResponse;
            if (response && response.data && response.data.type) {
                const apiProblem = (response.data as ApiProblemDetails);
                message = apiProblem.detail;
                title = apiProblem.title;
                errorList = apiProblem.errors;
                status = apiProblem.status;
                stackTrace = apiProblem.extensions && apiProblem.extensions["stackTrace"];
                traceIdentifier = apiProblem.extensions && apiProblem.extensions["traceIdentifier"];
            }
            if (response && typeof (response.data) === "string") {
                if (response.data.startsWith("Performa.Common.Infrastructure.Entityframework.Exceptions.EFInializationException")) {
                    const lines = response.data.split("\n\r");
                    message = lines[0].substring(lines[0].indexOf(":") + 1);
                    title = "Erro Durante Atualização da Base de Dados";
                }
                else {
                    message = response.data;
                    title = error.code;
                }
                status = error.response && error.response.status;
            }
            else {
                message = error.message;
                title = error.code;
                status = error.response && error.response.status;
                stackTrace = error.stack;
                if ((error as any).detail) errorList = { "detail": [(error as any).detail] } as ApiProblemErrors;
            }
        }
    }
    else if (typeof error === "string") {
        message = error;
    }
    else if ('name' in error) {
        message = error.message;
        stackTrace = error.stack;
        title = error.name;
    }
    else if ('type' in error) {
        if (error.type == "application/problem+json") {
            title = "Erro durante o Download";
            message = "Não foi possível obter o arquivo para download";
        }
        else {
            message = error.detail;
            title = error.title;
            errorList = error.errors;
            status = error.status;
            stackTrace = error.extensions && error.extensions["stackTrace"];
            traceIdentifier = error.extensions && error.extensions["traceIdentifier"];
        }
    }

    return {
        title,
        status,
        message,
        errorList,
        stackTrace,
        traceIdentifier
    };
};

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    error?: MprcRequestError | null;
    onRetry?: () => void;
    action?: React.ReactNode;
}

export const MprcRequestErrorReport: React.FC<IProps> = (props) => {
    const { error, action, onRetry } = props;

    if (error == undefined || error == null) return null;

    /////////////////////////////////////
    const e = ExtractErrorInformation(error);
    const debug = process.env.NODE_ENV == "development" || window.location.hostname === "locahost";
    /////////////////////////////////////

    return (
        <Grid container>
            <Grid size="grow">
                <MprcMessageBox
                    size="medium"
                    variant="error"
                    fullWidth
                    message={
                        <>
                            <Typography variant="h1" color="white">
                                <span>{e.title}</span>
                            </Typography>

                            <Typography variant="body1" component="p" color="white" sx={{ marginTop: 1 }}>
                                {e.message}
                            </Typography>

                            {
                                (e.errorList && Object.keys(e.errorList).length > 0) &&
                                <Typography variant="subtitle1" component="span" color="white" sx={{ marginTop: .5 }}>
                                    <ul style={{ paddingLeft: 16 }}>
                                        {
                                            Object.values(e.errorList).map((item) => {
                                                return item.map((x, i) => (<li key={i}>{x}</li>));
                                            })
                                        }
                                    </ul>
                                </Typography>
                            }
                        </>
                    }
                    action={
                        <>
                            {action}

                            {
                                e.status == 423 && onRetry &&
                                <ButtonRetry onClick={() => onRetry()} variant="contained" />
                            }
                        </>
                    }
                />

                {
                    debug && e.stackTrace &&
                    <Box mt={2} mb={2}>
                        <Card>
                            <CardContent>
                                <Typography variant="h1">Stack Trace</Typography>
                                <div style={{ whiteSpace: "pre-wrap", overflow: "auto", marginTop: 20 }}>
                                    <code>{e.stackTrace}</code>
                                </div>
                            </CardContent>
                        </Card>
                    </Box>
                }
            </Grid>
        </Grid>
    );
};
